import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/StatusBadge";
import { PageHeader } from "@/components/PageHeader";
import { formatDate, formatDateTime } from "@/lib/format";
import { LogIn, LogOut, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";

function getPosition() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
      () => resolve(null),
      { timeout: 8000 }
    );
  });
}

export default function Attendance() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);

  const { data: today, isLoading } = useQuery({ queryKey: ["attendance-today"], queryFn: async () => (await api.get("/attendance/today")).data });
  const { data: history = [] } = useQuery({ queryKey: ["attendance-history"], queryFn: async () => (await api.get("/attendance/history")).data });

  const act = async (kind) => {
    setBusy(true);
    try {
      const pos = await getPosition();
      const body = { latitude: pos?.latitude ?? 0, longitude: pos?.longitude ?? 0, address: pos ? undefined : "Location unavailable" };
      await api.post(`/attendance/${kind}`, body);
      qc.invalidateQueries({ queryKey: ["attendance-today"] });
      qc.invalidateQueries({ queryKey: ["attendance-history"] });
      toast.success(kind === "checkin" ? "Checked in" : "Checked out");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="attendance-page">
      <PageHeader title="Attendance" subtitle="Check in / out and view your attendance history." />

      <Card className="p-6 border-slate-200 dark:border-slate-800 mb-6">
        {isLoading ? <Skeleton className="h-20 w-full" /> : (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="text-sm text-slate-400">{formatDate(today?.date)}</div>
              <div className="flex items-center gap-3 mt-1">
                <StatusBadge status={today?.status} />
                <span className="text-sm text-slate-500">In: <span className="font-num text-slate-700 dark:text-slate-300">{today?.check_in_at ? formatDateTime(today.check_in_at) : "—"}</span></span>
                <span className="text-sm text-slate-500">Out: <span className="font-num text-slate-700 dark:text-slate-300">{today?.check_out_at ? formatDateTime(today.check_out_at) : "—"}</span></span>
              </div>
              {today?.half_day_reason && <div className="text-xs text-amber-600 mt-1">{today.half_day_reason}</div>}
            </div>
            <div className="flex gap-2">
              <Button onClick={() => act("checkin")} disabled={busy || !!today?.check_in_at} data-testid="attendance-checkin-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4 mr-1" />} Check In
              </Button>
              <Button onClick={() => act("checkout")} disabled={busy || !today?.check_in_at || !!today?.check_out_at} data-testid="attendance-checkout-btn" variant="outline">
                <LogOut className="h-4 w-4 mr-1" /> Check Out
              </Button>
            </div>
          </div>
        )}
        <div className="flex items-center gap-1 text-xs text-slate-400 mt-3"><MapPin className="h-3.5 w-3.5" /> Location is captured from your browser for verification.</div>
      </Card>

      <Card className="border-slate-200 dark:border-slate-800 overflow-hidden p-0">
        <Table data-testid="attendance-history-table">
          <TableHeader>
            <TableRow className="bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-50">
              <TableHead className="uppercase text-xs tracking-wider">Date</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Check In</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Check Out</TableHead>
              <TableHead className="uppercase text-xs tracking-wider text-right">Hours</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {history.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-10">No attendance records.</TableCell></TableRow>}
            {history.map((h) => (
              <TableRow key={h.id} data-testid={`attendance-row-${h.id}`}>
                <TableCell className="font-medium text-slate-900 dark:text-slate-100">{formatDate(h.date)}</TableCell>
                <TableCell className="font-num text-slate-600 dark:text-slate-300">{h.check_in_at ? formatDateTime(h.check_in_at) : "—"}</TableCell>
                <TableCell className="font-num text-slate-600 dark:text-slate-300">{h.check_out_at ? formatDateTime(h.check_out_at) : "—"}</TableCell>
                <TableCell className="text-right font-num">{h.duration_hours != null ? h.duration_hours.toFixed(1) : "—"}</TableCell>
                <TableCell><StatusBadge status={h.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
